import { useMemo, useState } from "react";
import { DoorOpen, X, CalendarPlus, CalendarDays, ListPlus } from "lucide-react";
import { planStatus, ELEMENT_META } from "@/components/property/floorPlanMeta";
import { ROOM_STATUS_LABELS, resolveRoomPrice } from "@/components/property/roomMeta";
import { AmenityIcon } from "@/components/property/amenityMeta";
import { photoSrc } from "@/lib/photos";

const CANVAS_W = 1400;
const CANVAS_H = 900;

// Read-only floor plan for customers. Room cards are minimalist: number + status
// color only. Clicking a room opens a detail popover with price/amenities/photos/booking.
export default function FloorPlanViewer({ elements = [], rooms = [], roomPhotos = {}, preferredPeriod = "month", onBook, onSurvey, onWaitlist }) {
  const [active, setActive] = useState(null); // room object

  const roomById = useMemo(() => rooms.reduce((a, r) => { a[r.id] = r; return a; }, {}), [rooms]);

  if (!elements || elements.length === 0) return null;

  const maxX = Math.min(CANVAS_W, Math.max(...elements.map((e) => e.x + (e.width || 0))) + 40);
  const maxY = Math.min(CANVAS_H, Math.max(...elements.map((e) => e.y + (e.height || 0))) + 40);

  const openRoom = (el) => {
    const r = roomById[el.room_id];
    if (r) setActive(r);
  };

  const rp = active ? resolveRoomPrice(active, preferredPeriod) : null;
  const photos = active ? (roomPhotos[active.id] || []) : [];

  return (
    <div data-testid="floorplan-viewer" className="space-y-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><span className="inline-block w-2.5 h-2.5 rounded bg-emerald-500 mr-1.5 align-middle" />Tersedia</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded bg-rose-500 mr-1.5 align-middle" />Terisi</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded bg-amber-400 mr-1.5 align-middle" />Akan tersedia</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded bg-gray-400 mr-1.5 align-middle" />Tidak tersedia</span>
      </div>

      <div className="overflow-auto rounded-2xl border border-border bg-[linear-gradient(to_right,rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.05)_1px,transparent_1px)] [background-size:20px_20px]" style={{ maxHeight: "60vh" }}>
        <div className="relative" style={{ width: Math.max(320, maxX), height: Math.max(240, maxY) }}>
          {elements.map((el) => {
            const isRoom = el.kind === "room";
            if (isRoom) {
              const st = planStatus(el.status);
              return (
                <button
                  key={el.key}
                  type="button"
                  onClick={() => openRoom(el)}
                  data-testid={`viewer-room-${el.room_id}`}
                  style={{ left: el.x, top: el.y, width: el.width, height: el.height, transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined }}
                  className={`absolute rounded-xl border-2 grid place-items-center font-semibold shadow-sm transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-primary/50 ${st.box}`}
                >
                  <span className="text-sm truncate px-1">{el.room_number || "Kamar"}</span>
                </button>
              );
            }
            const meta = ELEMENT_META[el.kind];
            const Icon = meta?.icon || DoorOpen;
            return (
              <div key={el.key}
                data-testid={`viewer-el-${el.key}`}
                style={{ left: el.x, top: el.y, width: el.width, height: el.height, transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined }}
                className="absolute rounded-xl border-2 border-dashed border-border bg-secondary/60 flex flex-col items-center justify-center gap-0.5 text-muted-foreground">
                <Icon size={16} />
                <span className="text-[10px] font-medium">{meta?.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Klik kotak kamar untuk melihat detail.</p>
      <p className="text-xs text-muted-foreground" data-testid="floorplan-waitlist-hint">
        Berminat dengan kamar yang sedang <span className="font-semibold text-rose-600">terisi</span> (kotak berwarna merah)? Klik kotak kamar merah tersebut, lalu tekan tombol <span className="font-semibold">Gabung Waiting List</span> untuk bergabung dalam antrean.
      </p>

      {active && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="viewer-room-modal" onClick={() => setActive(null)}>
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-semibold">Kamar {active.room_number}</h3>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${planStatus(active.status).box}`} data-testid="viewer-modal-status">
                  {ROOM_STATUS_LABELS[active.status] || active.status}
                </span>
              </div>
              <button onClick={() => setActive(null)} data-testid="viewer-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {photos.length > 0 && (
                <div className="flex gap-2 overflow-x-auto" data-testid="viewer-modal-photos">
                  {photos.slice(0, 6).map((ph) => (
                    <img key={ph.id} src={photoSrc(ph)} alt="foto kamar" className="shrink-0 w-28 h-20 rounded-lg object-cover border border-border" />
                  ))}
                </div>
              )}
              <div className="text-sm">
                <p data-testid="viewer-modal-price">
                  {rp?.unit ? <><span className="font-semibold text-primary text-base">{rp.label}</span> <span className="text-muted-foreground">{rp.suffix}</span></> : <span className="text-muted-foreground">Harga belum tersedia</span>}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-4 text-muted-foreground">
                  {active.size != null && <span>{active.size} m²</span>}
                  {active.floor != null && <span>Lantai {active.floor}</span>}
                </div>
              </div>
              {active.amenities?.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="viewer-modal-amenities">
                  {active.amenities.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs">
                      <AmenityIcon name={a.icon} size={12} className="text-primary" /> {a.name}
                    </span>
                  ))}
                </div>
              )}
              {active.status === "available" && onBook && (
                <button
                  onClick={() => { const r = active; setActive(null); onBook(r); }}
                  data-testid="viewer-modal-book"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
                  <CalendarPlus size={16} /> Booking Kamar Ini
                </button>
              )}
              {active.status === "available" && onSurvey && (
                <button
                  onClick={() => { const r = active; setActive(null); onSurvey(r); }}
                  data-testid="viewer-modal-survey"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 active:scale-95 transition-[transform,background-color]">
                  <CalendarDays size={16} /> Jadwalkan Survei
                </button>
              )}
              {active.status !== "available" && onWaitlist && (
                <div className="space-y-2">
                  <button
                    onClick={() => { const r = active; setActive(null); onWaitlist(r); }}
                    data-testid="viewer-modal-waitlist"
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
                    <ListPlus size={16} /> Gabung Waiting List
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    Kamar terisi — daftar antre agar dihubungi pemilik saat kamar tersedia.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
